// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { RotateCcw, Settings } from "lucide-react"
import { SessionSyncControls } from './SessionSyncControls'
import { SessionDisplay } from '@/components/SessionDisplay'
import { useSyncMutations } from '@/hooks/admin/useSyncMutations'
import { useSyncSettings } from '@/hooks/admin/useSyncSettings'
import type { APISession } from '@/types/session'
import type { DataSource } from '@/hooks/admin/useDatasourceState'
import { useState } from 'react'

interface SyncSettingsCardProps {
  selectedDataSource: DataSource
  apiSessions: APISession[]
  isExpanded: boolean
  onToggleExpanded: () => void
  expandedSessions: Record<string, boolean>
  onToggleSessionExpanded: (session: string) => void
}

export function SyncSettingsCard({
  selectedDataSource,
  apiSessions,
  isExpanded,
  onToggleExpanded,
  expandedSessions,
  onToggleSessionExpanded
}: SyncSettingsCardProps) {
  const { syncMarylandBills } = useSyncMutations()
  const { data: syncSettings, updateSyncSettings, isPending: isSyncSettingsUpdating } = useSyncSettings()
  
  // State for sync controls
  const [selectedSession, setSelectedSession] = useState<string>('')
  const [maxBills, setMaxBills] = useState<number>(10)
  const [maxBillsInput, setMaxBillsInput] = useState<string>('10')
  const [forceChanges, setForceChanges] = useState<boolean>(false)

  // Helper function to check if a session is enabled for sync
  const isSessionEnabled = (session: APISession): boolean => {
    if (!syncSettings?.session_sync) return false
    
    const dataSourceKey = selectedDataSource.toLowerCase() as keyof typeof syncSettings.session_sync
    const dataSourceSettings = syncSettings.session_sync[dataSourceKey]
    
    if (!dataSourceSettings?.sessions) return false
    
    return dataSourceSettings.sessions[session]?.sync_enabled ?? false
  }

  // Handle sync toggle for individual sessions
  const handleSyncToggle = (session: APISession, enabled: boolean) => {
    if (!syncSettings) return
    
    const dataSourceKey = selectedDataSource.toLowerCase() as keyof typeof syncSettings.session_sync
    const currentSettings = { ...syncSettings.session_sync }
    
    // Initialize data source settings if they don't exist
    if (!currentSettings[dataSourceKey]) {
      currentSettings[dataSourceKey] = { sessions: {} }
    }
    
    // Initialize sessions if they don't exist
    if (!currentSettings[dataSourceKey]!.sessions) {
      currentSettings[dataSourceKey]!.sessions = {}
    }
    
    // Update the session sync setting
    currentSettings[dataSourceKey]!.sessions[session] = {
      ...currentSettings[dataSourceKey]!.sessions[session],
      sync_enabled: enabled
    }
    
    updateSyncSettings({
      session_sync: currentSettings
    })
  }

  // Check if any mutation is in progress
  const isMutating = syncMarylandBills.isPending || isSyncSettingsUpdating

  return (
    <Card className="border shadow-sm">
      <CardHeader 
        className="pb-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
        onClick={onToggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls="sync-settings-content"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggleExpanded()
          }
        }}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {selectedDataSource} Sync Settings
          </CardTitle>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent id="sync-settings-content" className="pt-0 pb-3 space-y-4">
          {/* Sync Controls Section */}
          {selectedDataSource === 'Maryland' && (
            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800/50">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                Sync Bills
              </h3>
              <div className="space-y-3">
                {/* First row with main controls */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="space-y-2">
                  <Label htmlFor="session-select" className="text-xs text-slate-600 dark:text-slate-400">
                    Session
                  </Label>
                  <Select value={selectedSession} onValueChange={setSelectedSession}>
                    <SelectTrigger id="session-select" className="h-8">
                      <SelectValue placeholder="Select session" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sessions</SelectItem>
                      {apiSessions.map((session) => (
                        <SelectItem key={session} value={session}>
                          <SessionDisplay session={session} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="max-bills" className="text-xs text-slate-600 dark:text-slate-400">
                    Max Bills
                  </Label>
                  <Input
                    id="max-bills"
                    type="number"
                    min="1"
                    max="1000"
                    value={maxBillsInput}
                    onChange={(e) => {
                      const value = e.target.value
                      setMaxBillsInput(value)
                      
                      if (value === '') {
                        // Don't update maxBills when empty, keep the last valid value
                        return
                      }
                      
                      const numValue = parseInt(value)
                      if (!isNaN(numValue) && numValue >= 1) {
                        setMaxBills(numValue)
                      }
                    }}
                    onBlur={() => {
                      // On blur, ensure we have a valid value
                      const value = parseInt(maxBillsInput)
                      if (isNaN(value) || value < 1) {
                        setMaxBills(10)
                        setMaxBillsInput('10')
                      } else {
                        setMaxBills(value)
                        setMaxBillsInput(value.toString())
                      }
                    }}
                    className="h-8"
                    placeholder="10"
                  />
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const sessionParam = selectedSession === 'all' || selectedSession === '' ? undefined : selectedSession
                    syncMarylandBills.mutate({
                      session: sessionParam,
                      maxBills: maxBills,
                      force_changes: forceChanges
                    })
                  }}
                  disabled={syncMarylandBills.isPending || !selectedSession}
                  className="flex items-center gap-2 h-8"
                >
                  <RotateCcw className={`h-3 w-3 ${syncMarylandBills.isPending ? 'animate-spin' : ''}`} />
                  {syncMarylandBills.isPending ? 'Syncing...' : 'Start Sync'}
                </Button>
              </div>

              {/* Second row with force changes toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="force-changes" className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                    Force Changes
                  </Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Force processing all bills even when content hasn't changed
                  </p>
                </div>
                <Switch
                  id="force-changes"
                  checked={forceChanges}
                  onCheckedChange={setForceChanges}
                  disabled={syncMarylandBills.isPending}
                />
              </div>
              </div>
            </div>
          )}

          {/* Session Configuration Controls */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">Session Configuration</h3>
            {apiSessions.map((session) => {
              const sessionEnabled = isSessionEnabled(session)
              
              return (
                <SessionSyncControls
                  key={session}
                  session={session}
                  sessionEnabled={sessionEnabled}
                  isExpanded={expandedSessions[session] || false}
                  isMutating={isMutating}
                  onSyncToggle={handleSyncToggle}
                  onToggleExpanded={() => onToggleSessionExpanded(session)}
                />
              )
            })}
          </div>


          {syncSettings?.updated_at && (
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-green-500"></div>
                Updated by {syncSettings.updated_by} at {new Date(syncSettings.updated_at).toLocaleString()}
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
